import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, RefreshCw, Mail, Phone, FileText, MessageSquare,
  Package, Ship, CalendarDays, CreditCard, Download,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quoteFor, setQuoteFor] = useState(null);

  const load = async (tk) => {
    setLoading(true);
    try {
      const [inq, chat] = await Promise.all([
        axios.get(`${API}/inquiries`, { headers: { "X-Admin-Token": tk } }),
        axios.get(`${API}/chat/sessions`, { headers: { "X-Admin-Token": tk } }),
      ]);
      setItems(inq.data);
      setSessions(chat.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Invalid admin token.");
      else toast.error("Could not fetch data.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <form
          onSubmit={(e) => { e.preventDefault(); load(token); }}
          className="bg-white border border-[#1A3626]/10 p-10 md:p-12 w-full max-w-md"
          data-testid="admin-login-form"
        >
          <Lock className="text-[#C5A059]" size={28} strokeWidth={1.25} />
          <h1 className="mt-6 font-serif-display text-4xl text-[#1A3626]">Trade Desk Admin</h1>
          <p className="mt-2 text-sm text-[#4B5563]">Enter the admin token to view RFQ and chat inquiries.</p>
          <div className="mt-8">
            <Label className="text-xs uppercase tracking-widest text-[#1A3626]/70 mb-2 block">Admin token</Label>
            <Input
              data-testid="admin-token-input" type="password" value={token}
              onChange={(e) => setToken(e.target.value)} placeholder="••••••••"
              className="rounded-none border-[#1A3626]/25 focus-visible:ring-[#C5A059]" required
            />
          </div>
          <button type="submit" data-testid="admin-login-submit" className="mt-6 btn-primary w-full justify-center" disabled={loading}>
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline">Admin</p>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#1A3626]">Trade Desk</h1>
          </div>
          <button onClick={() => load(token)} className="btn-outline-dark !py-2 !px-4 text-sm" data-testid="admin-refresh">
            <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <Tabs defaultValue="rfq">
          <TabsList className="rounded-none bg-white border border-[#1A3626]/10 p-1">
            <TabsTrigger value="rfq" data-testid="tab-rfq" className="rounded-none data-[state=active]:bg-[#1A3626] data-[state=active]:text-white">
              <FileText size={14} className="mr-1.5" /> RFQ Inquiries ({items.length})
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" className="rounded-none data-[state=active]:bg-[#1A3626] data-[state=active]:text-white">
              <MessageSquare size={14} className="mr-1.5" /> Chat Inquiries ({sessions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rfq" className="mt-8">
            {items.length === 0 ? (
              <Empty label="No RFQ inquiries yet." />
            ) : (
              <div className="space-y-4" data-testid="admin-inquiry-list">
                {items.map((it) => (
                  <InquiryCard key={it.id} it={it} onQuote={() => setQuoteFor(it)} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="chat" className="mt-8">
            {sessions.length === 0 ? (
              <Empty label="No chat inquiries yet." />
            ) : (
              <div className="space-y-4" data-testid="admin-chat-list">
                {sessions.map((s) => <ChatCard key={s.session_id} s={s} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <QuotationDialog inquiry={quoteFor} token={token} onClose={() => setQuoteFor(null)} />
    </div>
  );
}

function Empty({ label }) {
  return <div className="bg-white border border-[#1A3626]/10 p-12 text-center text-[#4B5563]">{label}</div>;
}

function InquiryCard({ it, onQuote }) {
  const product = it.product_name || it.commodity || "—";
  const dest = it.destination_port || it.destination || "—";
  const qty = [it.quantity, it.unit].filter(Boolean).join(" ") || "—";
  return (
    <div className="bg-white border border-[#1A3626]/10 p-6 md:p-8" data-testid={`admin-inquiry-item-${it.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="overline text-[#C5A059]">{it.source || "rfq"}</span>
            <span className="text-xs text-[#4B5563]">{new Date(it.created_at).toLocaleString()}</span>
          </div>
          <h3 className="mt-2 font-serif-display text-2xl text-[#1A3626]">
            {it.name}
            {it.company ? <span className="text-[#4B5563] text-lg font-sans-body font-normal ml-2">· {it.company}</span> : null}
          </h3>
        </div>
        <div className="text-sm text-[#4B5563] space-y-1">
          <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.5} className="text-[#C5A059]" /> {it.email}</div>
          {it.phone && <div className="flex items-center gap-2"><Phone size={14} strokeWidth={1.5} className="text-[#C5A059]" /> {it.phone}</div>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Meta icon={Package} label="Product" value={product} />
        <Meta icon={FileText} label="Quantity" value={qty} />
        <Meta icon={Ship} label="Destination" value={dest} />
        <Meta icon={Package} label="Packaging" value={it.packaging_type || "—"} />
        <Meta icon={CreditCard} label="Payment Terms" value={it.payment_terms || "—"} />
        <Meta icon={CalendarDays} label="Delivery Date" value={it.delivery_date || "—"} />
      </div>

      {it.message && (
        <div className="mt-5 pt-5 border-t border-[#1A3626]/10 text-[#374151] leading-relaxed text-sm whitespace-pre-wrap">{it.message}</div>
      )}

      <div className="mt-5 pt-5 border-t border-[#1A3626]/10">
        <button onClick={onQuote} data-testid={`generate-quote-${it.id}`} className="btn-gold !py-2 !px-5 text-sm">
          <FileText size={14} strokeWidth={1.5} /> Create Quotation
        </button>
      </div>
    </div>
  );
}

function ChatCard({ s }) {
  return (
    <div className="bg-white border border-[#1A3626]/10 p-6 md:p-8" data-testid={`admin-chat-item-${s.session_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="overline text-[#C5A059]">Chat</span>
          <h3 className="mt-2 font-serif-display text-2xl text-[#1A3626]">{s.name || "Anonymous visitor"}</h3>
          {s.company && <p className="text-sm text-[#4B5563]">{s.company}</p>}
        </div>
        <div className="text-sm text-[#4B5563] space-y-1">
          {s.email && <div className="flex items-center gap-2"><Mail size={14} className="text-[#C5A059]" /> {s.email}</div>}
          {s.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-[#C5A059]" /> {s.phone}</div>}
          <div className="text-xs">{s.last_message_at ? new Date(s.last_message_at).toLocaleString() : ""}</div>
        </div>
      </div>
      <div className="mt-5 pt-5 border-t border-[#1A3626]/10 space-y-2 max-h-72 overflow-y-auto">
        {(s.messages || []).map((m, i) => (
          <div key={i} className={`text-sm ${m.role === "user" ? "text-[#1A3626]" : "text-[#4B5563]"}`}>
            <span className="overline text-[10px] mr-2">{m.role === "user" ? "Buyer" : "AI"}</span>
            {m.content}
          </div>
        ))}
        {(!s.messages || s.messages.length === 0) && <p className="text-sm text-[#4B5563]">No messages recorded.</p>}
      </div>
    </div>
  );
}

function Meta({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="overline text-[#1A3626]/60 flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={1.5} className="text-[#C5A059]" />}{label}
      </div>
      <div className="mt-1 text-[#1A3626]">{value}</div>
    </div>
  );
}

const CURRENCIES = ["USD", "EUR", "GBP", "AED", "INR", "SGD", "SAR"];

function QuotationDialog({ inquiry, token, onClose }) {
  const [q, setQ] = useState({});
  const [busy, setBusy] = useState(false);
  const open = !!inquiry;

  useEffect(() => {
    if (inquiry) {
      setQ({
        currency: "USD",
        quantity: inquiry.quantity || "",
        unit: inquiry.unit || "",
        unit_price: "",
        total_amount: "",
        payment_terms: inquiry.payment_terms || "",
        incoterms: "",
        validity_date: "",
        bank_details: "",
        notes: "",
      });
    }
  }, [inquiry]);

  const upd = (k) => (e) => setQ((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const generate = async () => {
    if (!q.unit_price || !q.total_amount) {
      toast.error("Enter unit price and total amount.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/quotations`, {
        inquiry_id: inquiry.id,
        currency: q.currency, quantity: q.quantity, unit: q.unit,
        unit_price: q.unit_price, total_amount: q.total_amount,
        payment_terms: q.payment_terms, incoterms: q.incoterms,
        validity_date: q.validity_date, bank_details: q.bank_details, notes: q.notes,
      }, { headers: { "X-Admin-Token": token } });

      const quote = res.data;
      const pdf = await axios.get(`${API}/quotations/${quote.id}/pdf`, {
        headers: { "X-Admin-Token": token }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote.quote_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Quotation ${quote.quote_number} generated & downloaded.`);
      onClose();
    } catch (e) {
      toast.error("Could not generate quotation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quotation-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1A3626]">
            Pro-forma Quotation
          </DialogTitle>
          <p className="text-sm text-[#4B5563]">
            For {inquiry?.name} · {inquiry?.product_name || inquiry?.commodity}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <QField label="Currency">
            <Select value={q.currency} onValueChange={upd("currency")}>
              <SelectTrigger data-testid="quote-currency" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </QField>
          <QField label="Validity Date">
            <Input data-testid="quote-validity" type="date" value={q.validity_date || ""} onChange={upd("validity_date")} className="rounded-none" />
          </QField>
          <QField label="Quantity">
            <Input data-testid="quote-quantity" value={q.quantity || ""} onChange={upd("quantity")} placeholder="25" className="rounded-none" />
          </QField>
          <QField label="Unit">
            <Input data-testid="quote-unit" value={q.unit || ""} onChange={upd("unit")} placeholder="MT" className="rounded-none" />
          </QField>
          <QField label="Unit Price *">
            <Input data-testid="quote-unit-price" value={q.unit_price || ""} onChange={upd("unit_price")} placeholder="1250" className="rounded-none" />
          </QField>
          <QField label="Total Amount *">
            <Input data-testid="quote-total" value={q.total_amount || ""} onChange={upd("total_amount")} placeholder="31250" className="rounded-none" />
          </QField>
          <QField label="Incoterms" full>
            <Input data-testid="quote-incoterms" value={q.incoterms || ""} onChange={upd("incoterms")} placeholder="CIF Jebel Ali" className="rounded-none" />
          </QField>
          <QField label="Payment Terms" full>
            <Input data-testid="quote-payment" value={q.payment_terms || ""} onChange={upd("payment_terms")} placeholder="30% advance TT, balance against BL" className="rounded-none" />
          </QField>
          <QField label="Bank Details" full>
            <Textarea data-testid="quote-bank" value={q.bank_details || ""} onChange={upd("bank_details")} rows={3} placeholder="Beneficiary, Bank, A/C no., SWIFT, IFSC" className="rounded-none" />
          </QField>
          <QField label="Notes" full>
            <Textarea data-testid="quote-notes" value={q.notes || ""} onChange={upd("notes")} rows={2} placeholder="Optional notes" className="rounded-none" />
          </QField>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="btn-outline-dark !py-2 !px-5 text-sm">Cancel</button>
          <button onClick={generate} disabled={busy} data-testid="quote-generate-pdf" className="btn-primary !py-2 !px-5 text-sm disabled:opacity-60">
            <Download size={14} strokeWidth={1.5} /> {busy ? "Generating..." : "Generate & Download PDF"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QField({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-[#1A3626]/70 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
